import { Dispatch } from 'redux';

import { displayNotification } from 'components/layout/notifications/notificationsSdk';
import config from 'config';
import { fullLogoutAndDispatch } from 'reduxState/currentUser/currentUserUtils';
import { staticDispatch } from 'reduxState/store';
import { ReduxActions } from 'reduxState/storeCore';
import { IWallet } from 'reduxState/wallet/walletReducer';

import {
  DelegatedKeyAuthorization,
  delegatedKeyUpdateNotified,
} from './authorizationReducer';
import { DelegatedStorageEngine } from './settingsReducer';

const DAY_MS = 1000 * 60 * 60 * 24;

export const { delegatedKeyExpirationDays } = config;

const expireMs = DAY_MS * delegatedKeyExpirationDays;

export const NOTICE_1_BEFORE_EXP_DAYS = 3;
export const NOTICE_2_BEFORE_EXP_DAYS = 1;

const notice1Ms =
  DAY_MS * (delegatedKeyExpirationDays - NOTICE_1_BEFORE_EXP_DAYS);
const notice2Ms =
  DAY_MS * (delegatedKeyExpirationDays - NOTICE_2_BEFORE_EXP_DAYS);

export function checkDelegatedExpiring(
  createdTime: number,
  /** "warnFirst/Last" will be "valid" if already notified */
  lastRemindedTs: number
): {
  state: 'valid' | 'warnFirst' | 'warnLast' | 'expired';
  remainingTime?: string;
  expireDate: number;
} {
  const now = Date.now();

  const expireDate = createdTime + expireMs;
  if (now >= expireDate) {
    return {
      state: 'expired',
      remainingTime: undefined,
      expireDate,
    };
    // logoutAndDispatch(wallet, undefined, staticDispatch);
    // displayNotification({ type: 'delegatedKeyExpired', time: Date.now() });
  }
  const notify1stDate = createdTime + notice1Ms;
  const notify2ndDate = createdTime + notice2Ms;

  const lastWarn = now >= notify2ndDate && lastRemindedTs < notify2ndDate;
  const firstWarn = now >= notify1stDate && lastRemindedTs < notify1stDate;

  const remainingHours = (expireDate - now) / 1000 / 60 / 60;
  const remainingDays = remainingHours / 24;

  const remainingTime =
    remainingHours < 1
      ? 'few minutes'
      : remainingHours >= 24
      ? `${Math.floor(remainingDays)} day${remainingDays < 2 ? '' : 's'}`
      : `${Math.floor(remainingHours)} hour${remainingHours < 2 ? '' : 's'}`;

  if (lastWarn || firstWarn) {
    return {
      state: lastWarn ? 'warnLast' : 'warnFirst',
      expireDate,
      remainingTime,
    };
  }
  return {
    state: 'valid',
    expireDate,
    remainingTime,
  };
}

export function checkAndDisplayDelegatedExpiringNotification({
  createdTime,
  wallet,
  delegatedWallet,
  delegatedEngine,
  lastRenewNoticeTime,
  dispatch,
}: {
  createdTime: number;
  wallet: IWallet;
  delegatedWallet: DelegatedKeyAuthorization;
  delegatedEngine: DelegatedStorageEngine;
  lastRenewNoticeTime: number | undefined;
  dispatch: Dispatch<ReduxActions>;
}) {
  if (!createdTime || !wallet.walletType || !delegatedWallet.walletAddress) {
    return;
  }

  const keyState = checkDelegatedExpiring(
    createdTime,
    lastRenewNoticeTime || 0
  );

  if (keyState.state === 'expired') {
    const previousWallet = { ...wallet };
    fullLogoutAndDispatch(wallet, delegatedWallet, dispatch);
    displayNotification({
      type: 'delegatedKeyExpired',
      time: Date.now(),
      walletType: previousWallet.walletType,
      walletRdns: previousWallet.walletDiscoveryRdns,
      walletRemember: delegatedEngine === 'persistent',
    });
    return;
  }

  if (
    (keyState.state === 'warnFirst' || keyState.state === 'warnLast') &&
    keyState.remainingTime
  ) {
    staticDispatch(delegatedKeyUpdateNotified());
    displayNotification({
      type: 'delegatedKeyExpiringNotice',
      time: Date.now(),
      remainingTime: keyState.remainingTime,
      walletRemember: delegatedEngine === 'persistent',
      walletType: wallet.walletType,
      walletRdns: wallet.walletDiscoveryRdns,
    });
  }
}
